import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useMerchantPayoutFlags } from '../../hooks/useMerchantPayoutFlags';
import LogoutModal from '../../components/LogoutModal';
import WalletCreditsModal from '../../components/WalletCreditsModal';
import SuccessModal from '../../components/SuccessModal';
import TransactionStatusModal from '../../components/TransactionStatusModal';
import TransactionFeesModal from '../../components/TransactionFeesModal';
import TpinModal from '../../components/TpinModal';
import Footer from '../../components/Footer';

interface PayoutFormData {
  name: string;
  mobile: string;
  accountNumber: string;
  email: string;
  amount: string;
  ifscCode: string;
  bankName: string;
}

const MerchantNewPayoutsPoolPay: React.FC = () => {
  const { user, logout } = useAuth();
  const { viyonaEnabled, poolpayEnabled } = useMerchantPayoutFlags();
  const [activeTab, setActiveTab] = useState('create-payout-v2');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showTransactionFeesModal, setShowTransactionFeesModal] = useState(false);
  const [showTpinModal, setShowTpinModal] = useState(false);
  const [tpinError, setTpinError] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [transactionFeesData, setTransactionFeesData] = useState<any>(null);
  const [formData, setFormData] = useState<PayoutFormData>({
    name: '',
    mobile: '',
    accountNumber: '',
    email: '',
    amount: '',
    ifscCode: '',
    bankName: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<PayoutFormData>>({});
  const [ifscLoading, setIfscLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [transferType, setTransferType] = useState<'IMPS' | 'RTGS' | 'UPI'>('IMPS');
  const [remarkMode, setRemarkMode] = useState<'TEST' | 'REAL'>('TEST');
  const [statusCheckRef, setStatusCheckRef] = useState('');
  const [statusCheckLoading, setStatusCheckLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusResult, setStatusResult] = useState<any>(null);

  const merchantSidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'account', label: 'My Account', icon: '👤' },
    { id: 'payouts', label: 'Payouts', icon: '💰' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    ...(viyonaEnabled ? [{ id: 'create-payout' as const, label: 'Create Payout (v1 Viyona)', icon: '➕' }] : []),
    ...(poolpayEnabled ? [{ id: 'create-payout-v2' as const, label: 'Create Payout (v2 PoolPay)', icon: '💠' }] : []),
    { id: 'logout', label: 'Logout', icon: '🚪' }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch {
      /* ignore */
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Redirect if PoolPay payout is disabled by manager
  useEffect(() => {
    if (!poolpayEnabled) {
      window.location.href = '/merchant';
    }
  }, [poolpayEnabled]);

  const handleNavigation = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'home') {
      window.location.href = '/merchant';
    } else if (tabId === 'account') {
      window.location.href = '/merchant/account';
    } else if (tabId === 'payouts') {
      window.location.href = '/merchant/payouts';
    } else if (tabId === 'reports') {
      window.location.href = '/merchant/reports';
    } else if (tabId === 'create-payout') {
      window.location.href = '/merchant/new-payouts';
    } else if (tabId === 'create-payout-v2') {
      window.location.href = '/merchant/new-payouts/poolpay';
    } else if (tabId === 'logout') {
      setShowLogoutModal(true);
    }
    setSidebarOpen(false);
  };

  const handleCreditMoney = () => {
    setShowCreditsModal(true);
  };

  const handleNewPayout = () => {
    setFormData({
      name: '',
      mobile: '',
      accountNumber: '',
      email: '',
      amount: '',
      ifscCode: '',
      bankName: ''
    });
    setErrors({});
    setTransferType('IMPS');
    setRemarkMode('TEST');
  };

  const handleMyProfile = () => {
    window.location.href = '/merchant/account';
  };

  const handleInputChange = (field: keyof PayoutFormData, value: string) => {
    if (field === 'name') {
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
      const limitedValue = filteredValue.slice(0, 25);
      setFormData(prev => ({ ...prev, [field]: limitedValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    if (field === 'ifscCode') {
      const ifscCode = value.toUpperCase().trim();
      if (ifscCode.length === 11) {
        validateAndFetchBankName(ifscCode);
      }
    }
  };

  const validateAndFetchBankName = async (ifscCode: string) => {
    if (!ifscCode.trim()) {
      setErrors(prev => ({ ...prev, ifscCode: '' }));
      return;
    }

    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
      setErrors(prev => ({ ...prev, ifscCode: 'Invalid IFSC code format' }));
      return;
    }

    setIfscLoading(true);
    try {
      const response = await fetch(`https://ifsc.razorpay.com/${ifscCode}`);
      if (response.ok) {
        const bankData = await response.json();
        if (bankData.BANK && bankData.BANK !== '') {
          setFormData(prev => ({ ...prev, bankName: bankData.BANK }));
          setErrors(prev => ({ ...prev, ifscCode: '', bankName: '' }));
        } else {
          setErrors(prev => ({ ...prev, ifscCode: 'Bank not found for this IFSC code' }));
        }
      } else {
        setErrors(prev => ({ ...prev, ifscCode: 'Invalid IFSC code or bank not found' }));
      }
    } catch {
      setErrors(prev => ({ ...prev, ifscCode: 'Failed to validate IFSC code' }));
    } finally {
      setIfscLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<PayoutFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 25) {
      newErrors.name = 'Name must be maximum 25 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      newErrors.name = 'Name can only contain alphabets and spaces';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.trim())) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!/^\d{9,18}$/.test(formData.accountNumber.trim())) {
      newErrors.accountNumber = 'Please enter a valid account number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else if (!/^\d+(\.\d{1,2})?$/.test(formData.amount.trim())) {
      newErrors.amount = 'Please enter a valid amount';
    } else {
      const amountValue = parseFloat(formData.amount);
      if (amountValue < 100) {
        newErrors.amount = 'Minimum transfer amount is ₹100';
      } else if (amountValue > 25000) {
        newErrors.amount = 'Maximum transfer amount for PoolPay is ₹25,000';
      }
    }

    if (!formData.ifscCode.trim()) {
      newErrors.ifscCode = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.trim().toUpperCase())) {
      newErrors.ifscCode = 'Please enter a valid IFSC code';
    }

    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setCountdown(3);

    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setTimeout(async () => {
      try {
        const feesResponse = await api.get(`/merchants/transaction-fees?amount=${formData.amount}`);
        const feesData = feesResponse.data.data;
        setTransactionFeesData(feesData);
        setShowTransactionFeesModal(true);
        setIsSubmitting(false);
        return;
      } catch (error: any) {
        const errorMsg = error.response?.data?.message || 'Error submitting payout. Please try again.';
        setModalMessage(errorMsg);
        setShowErrorModal(true);
        setIsSubmitting(false);
      }
    }, 3000);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    window.location.href = '/merchant/payouts';
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
  };

  const handleTransactionFeesConfirm = async () => {
    setShowTransactionFeesModal(false);
    setLoading(true);
    setTpinError('');

    try {
      const tpinRes = await api.post('/merchants/send-tpin');
      if (!tpinRes.data.success) {
        setModalMessage(tpinRes.data.message || 'Failed to send TPIN. Please ensure your mobile number is updated in your profile.');
        setShowErrorModal(true);
        setLoading(false);
        return;
      }
      setShowTpinModal(true);
    } catch (err: any) {
      setModalMessage(err.response?.data?.message || 'Failed to send TPIN. Please try again.');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTpinSubmit = async (tpin: string) => {
    setTpinError('');
    setLoading(true);

    try {
      const remarkText = remarkMode === 'TEST' ? 'Test Payout Mode' : 'Real Payout Mode';

      const response = await api.post('/v2/poolpay/merchants/new-payouts', {
        merchant_reference_number: `POOLPAY_${Date.now()}`,
        account_name: formData.name,
        amount: parseFloat(formData.amount),
        account_number: formData.accountNumber,
        ifsc_code: formData.ifscCode.toUpperCase(),
        bank_name: formData.bankName,
        transfer_type: transferType,
        customer_email: formData.email,
        customer_phone: formData.mobile,
        remark: remarkText,
        tpin
      });

      setShowTpinModal(false);

      setFormData({
        name: '',
        mobile: '',
        accountNumber: '',
        email: '',
        amount: '',
        ifscCode: '',
        bankName: ''
      });

      const successMsg = response.data.message || 'PoolPay payout request submitted successfully!';
      const transactionId = response.data.data?.transaction_id || response.data.data?.id;
      const fullMessage = transactionId ? `${successMsg}\n\nTransaction ID: ${transactionId}` : successMsg;

      setModalMessage(fullMessage);
      setShowSuccessModal(true);
    } catch (error: any) {
      const res = error.response?.data;
      let errMsg = res?.message || 'Error submitting PoolPay payout. Please try again.';
      if (res?.error) errMsg += ` (${res.error})`;
      if (res?.data && typeof res.data === 'object' && (res.data.message || res.data.error)) {
        errMsg += ` — ${res.data.message || res.data.error}`;
      } else if (res?.data && typeof res.data === 'string') {
        errMsg += ` — ${res.data}`;
      }
      setTpinError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleTransactionFeesCancel = () => {
    setShowTransactionFeesModal(false);
    setTransactionFeesData(null);
  };

  const handleStatusCheck = async () => {
    if (!statusCheckRef.trim()) {
      setModalMessage('Please enter an order ID / merchant reference number');
      setShowErrorModal(true);
      return;
    }

    setStatusCheckLoading(true);

    try {
      const response = await api.get(`/v2/poolpay/merchants/status/${encodeURIComponent(statusCheckRef.trim())}`);
      setStatusResult(response.data);
      setShowStatusModal(true);
      setStatusCheckRef('');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Error checking transaction status. Please try again.';
      setModalMessage(errorMsg);
      setShowErrorModal(true);
    } finally {
      setStatusCheckLoading(false);
    }
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusResult(null);
  };

  const inputBase =
    'w-full min-w-0 py-2.5 px-4 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 box-border';
  const inputError = 'border-red-500 focus:border-red-500 focus:ring-red-500/20';

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-slate-100 to-slate-200">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} aria-hidden />}
      <aside
        id="merchant-sidebar"
        className="fixed top-0 left-0 z-50 h-full w-64 bg-[#0b0d32] text-slate-100 shadow-xl transition-transform duration-200 ease-out"
        style={isDesktop ? { transform: 'translateX(0)' } : sidebarOpen ? { transform: 'translateX(0)' } : { transform: 'translateX(-100%)' }}
      >
        <div className="p-5 border-b border-slate-600">
          <div className="flex items-center gap-2">
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo4.png`}
              alt="YuvanAPI"
              className="h-[3.75rem] w-auto max-w-[19rem] object-contain object-left"
            />
          </div>
        </div>
        <nav className="p-4 flex flex-col gap-1">
          {merchantSidebarItems.map(item => (
            <button
              key={item.id}
              type="button"
              className={`flex items-center gap-3 w-full py-3 px-4 rounded-lg text-left font-medium transition-colors ${
                activeTab === item.id ? 'bg-violet-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
              onClick={() => handleNavigation(item.id)}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="leading-tight">
                {item.id === 'create-payout' ? (
                  <>
                    <span className="block">Create Payout</span>
                    <span className="block text-xs opacity-90">(v1 Viyona)</span>
                  </>
                ) : item.id === 'create-payout-v2' ? (
                  <>
                    <span className="block">Create Payout</span>
                    <span className="block text-xs opacity-90">(v2 PoolPay)</span>
                  </>
                ) : (
                  <span className="block">{item.label}</span>
                )}
              </span>
            </button>
          ))}
        </nav>
      </aside>

      <header
        className="flex items-center gap-4 p-4 bg-[#0b0d32] text-white shadow sticky top-0 z-30"
        style={isDesktop ? { display: 'none' } : undefined}
      >
        <button type="button" className="p-2 rounded-lg hover:bg-slate-700" onClick={toggleSidebar} aria-label="Menu">
          ☰
        </button>
        <img
          src={`${process.env.PUBLIC_URL || ''}/logo4.png`}
          alt="YuvanAPI"
          className="h-14 w-auto max-w-[17rem] object-contain"
        />
      </header>

      <main id="merchant-main" className="flex-1 p-6 pt-8" style={isDesktop ? { marginLeft: '16rem' } : undefined}>
        <div
          className="flex flex-col gap-4 mb-6"
          style={isDesktop ? { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' } : undefined}
        >
          <h1 className="text-2xl font-bold text-slate-800 m-0 shrink-0">Welcome to YuvanAPI Merchant!</h1>
          <div className="flex flex-wrap gap-2" style={{ flexShrink: 0 }}>
            <button
              type="button"
              onClick={handleCreditMoney}
              className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-blue-700 whitespace-nowrap shrink-0"
            >
              💳 CREDIT MONEY
            </button>
            <button
              type="button"
              onClick={handleNewPayout}
              className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 whitespace-nowrap shrink-0"
            >
              💰 + Payout
            </button>
            <button
              type="button"
              onClick={handleMyProfile}
              className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-slate-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-slate-700 whitespace-nowrap shrink-0"
            >
              👤 My Profile
            </button>
            <button
              type="button"
              onClick={handleLogoutClick}
              className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-red-700 whitespace-nowrap shrink-0"
            >
              🚪 Logout
            </button>
          </div>
        </div>

        <div
          className="grid grid-cols-1 gap-6"
          style={isDesktop ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1.5rem' } : undefined}
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm min-w-0">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-2xl">📋</span>
              <div>
                <h3 className="text-lg font-bold text-slate-800 m-0">Payout Details</h3>
                <p className="text-sm text-slate-600 m-0">PoolPay v2 – fill beneficiary information below</p>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div
                className="grid grid-cols-1 gap-4"
                style={isDesktop ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : undefined}
              >
                <div className="flex flex-col gap-1">
                  <label htmlFor="name" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                    👤 Holder&apos;s Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    placeholder="Enter beneficiary's full name"
                    value={formData.name}
                    onChange={e => handleInputChange('name', e.target.value)}
                    className={`${inputBase} border-slate-200 ${errors.name ? inputError : ''}`}
                  />
                  {errors.name && <span className="text-red-600 text-sm">{errors.name}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="mobile" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                    📱 Mobile Number
                  </label>
                  <input
                    type="tel"
                    id="mobile"
                    placeholder="Enter 10-digit mobile number"
                    value={formData.mobile}
                    onChange={e => handleInputChange('mobile', e.target.value)}
                    className={`${inputBase} border-slate-200 ${errors.mobile ? inputError : ''}`}
                  />
                  {errors.mobile && <span className="text-red-600 text-sm">{errors.mobile}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="email" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                    📧 Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    className={`${inputBase} border-slate-200 ${errors.email ? inputError : ''}`}
                  />
                  {errors.email && <span className="text-red-600 text-sm">{errors.email}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="accountNumber" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                    🔢 Account Number
                  </label>
                  <input
                    type="text"
                    id="accountNumber"
                    placeholder="Enter account number"
                    value={formData.accountNumber}
                    onChange={e => handleInputChange('accountNumber', e.target.value)}
                    className={`${inputBase} border-slate-200 ${errors.accountNumber ? inputError : ''}`}
                  />
                  {errors.accountNumber && <span className="text-red-600 text-sm">{errors.accountNumber}</span>}
                </div>
                <div className="flex flex-col gap-1" style={isDesktop ? { gridColumn: 'span 2' } : undefined}>
                  <label htmlFor="ifscCode" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                    🏛️ IFSC Code
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="ifscCode"
                      placeholder="Enter IFSC code (e.g., SBIN0001234)"
                      value={formData.ifscCode}
                      onChange={e => handleInputChange('ifscCode', e.target.value.toUpperCase())}
                      className={`${inputBase} border-slate-200 ${errors.ifscCode ? inputError : ''}`}
                      disabled={ifscLoading}
                    />
                    {ifscLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-slate-200 border-t-violet-500 rounded-full animate-spin" />
                    )}
                  </div>
                  {errors.ifscCode && <span className="text-red-600 text-sm">{errors.ifscCode}</span>}
                </div>
                <div className="flex flex-col gap-1" style={isDesktop ? { gridColumn: 'span 2' } : undefined}>
                  <label htmlFor="bankName" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                    🏪 Bank Name
                  </label>
                  <input
                    type="text"
                    id="bankName"
                    placeholder="Enter bank name"
                    value={formData.bankName}
                    onChange={e => handleInputChange('bankName', e.target.value)}
                    className={`${inputBase} border-slate-200 ${errors.bankName ? inputError : ''}`}
                  />
                  {errors.bankName && <span className="text-red-600 text-sm">{errors.bankName}</span>}
                </div>
                <div className="flex flex-col gap-1" style={isDesktop ? { gridColumn: 'span 2' } : undefined}>
                  <label htmlFor="amount" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                    ₹ Transfer Amount
                  </label>
                  <input
                    type="number"
                    id="amount"
                    placeholder="100.00"
                    value={formData.amount}
                    onChange={e => handleInputChange('amount', e.target.value)}
                    className={`${inputBase} border-slate-200 ${errors.amount ? inputError : ''}`}
                    step="0.01"
                    min={100}
                    max={25000}
                  />
                  {errors.amount && <span className="text-red-600 text-sm">{errors.amount}</span>}
                  {!errors.amount && (
                    <span className="text-slate-500 text-xs">
                      Minimum transfer amount: ₹100.00, Maximum: ₹25,000.00
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1" style={isDesktop ? { gridColumn: 'span 2' } : undefined}>
                  <label htmlFor="transferMode" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                    Transfer Mode
                  </label>
                  <select
                    id="transferMode"
                    value={transferType}
                    onChange={e => setTransferType(e.target.value as 'IMPS' | 'RTGS' | 'UPI')}
                    className="w-full min-w-0 py-2.5 px-4 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
                  >
                    <option value="IMPS">IMPS (Instant)</option>
                    <option value="RTGS">RTGS</option>
                    <option value="UPI">UPI</option>
                  </select>
                  <span className="text-slate-500 text-xs">Matches PoolPay transfer_mode (IMPS, RTGS, UPI).</span>
                </div>
                <div className="flex flex-col gap-1" style={isDesktop ? { gridColumn: 'span 2' } : undefined}>
                  <span className="text-sm font-semibold text-slate-700 flex items-center gap-1">Remark Mode</span>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-700">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="remarkMode"
                        value="TEST"
                        checked={remarkMode === 'TEST'}
                        onChange={() => setRemarkMode('TEST')}
                      />
                      <span>Test Remark (uses "Test Payout Mode")</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="remarkMode"
                        value="REAL"
                        checked={remarkMode === 'REAL'}
                        onChange={() => setRemarkMode('REAL')}
                      />
                      <span>Real Remark (uses "Real Payout Mode")</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading || isSubmitting}
                  className="flex items-center justify-center gap-2 w-full py-3 px-5 rounded-lg bg-violet-600 text-white font-semibold text-sm border-none cursor-pointer hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span>{isSubmitting ? '⏳' : loading ? '⏳' : '💸'}</span>
                  <span>{isSubmitting ? `Submitting in ${countdown}s...` : loading ? 'Processing...' : 'Submit Payout'}</span>
                  {(loading || isSubmitting) && <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                </button>
              </div>
            </form>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm h-fit min-w-0">
            <h3 className="text-lg font-bold text-slate-800 m-0 mb-4">Check Transaction Status (PoolPay)</h3>
            <div className="flex flex-col gap-2" style={isDesktop ? { flexDirection: 'row' } : undefined}>
              <input
                type="text"
                placeholder="Enter PoolPay Order ID / Merchant Ref"
                value={statusCheckRef}
                onChange={e => setStatusCheckRef(e.target.value)}
                className="flex-1 py-2.5 px-4 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
              />
              <button
                type="button"
                onClick={handleStatusCheck}
                disabled={statusCheckLoading}
                className="py-2.5 px-5 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 disabled:opacity-60"
              >
                {statusCheckLoading ? 'Checking...' : 'Check Status'}
              </button>
            </div>
          </div>
        </div>
      </main>

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole={user?.role}
      />

      <WalletCreditsModal isOpen={showCreditsModal} onClose={() => setShowCreditsModal(false)} />

      <SuccessModal isOpen={showSuccessModal} onClose={handleSuccessModalClose} title="Success!" message={modalMessage} />

      <SuccessModal isOpen={showErrorModal} onClose={handleErrorModalClose} title="Error" message={modalMessage} />

      <TransactionStatusModal isOpen={showStatusModal} onClose={handleStatusModalClose} statusData={statusResult} />

      {transactionFeesData && (
        <TransactionFeesModal
          isOpen={showTransactionFeesModal}
          onClose={handleTransactionFeesCancel}
          onConfirm={handleTransactionFeesConfirm}
          feesData={transactionFeesData}
          beneficiaryName={formData.name}
          beneficiaryAccount={formData.accountNumber}
        />
      )}

      <TpinModal
        isOpen={showTpinModal}
        onClose={() => { setShowTpinModal(false); setTpinError(''); }}
        onSubmit={handleTpinSubmit}
        isLoading={loading}
        error={tpinError}
        maskedMobile={((user as any)?.details?.phone ?? (user as any)?.phone) ? String((user as any)?.details?.phone ?? (user as any)?.phone).slice(-4) : ''}
      />

      <Footer />
    </div>
  );
};

export default MerchantNewPayoutsPoolPay;

